import { getUserDisplayName } from "../lib/userDisplayName";
import { ToolNavigation } from "./ToolNavigation";
import { ToolSearchProvider, ToolSwitcher } from "./ToolSwitcher";
import { SITE_INFO, SITE_CONTACTS, DEVELOPER_SITES } from "../lib/siteInfo";
import * as React from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { NAV_ITEMS } from "../app/nav";
import { cn } from "../lib/cn";
import { Bell, Coins, Gift, Menu, Settings, X, User, LogOut, Mail, NotebookPen, MessageCircle, Users, BarChart3, Activity, School, Megaphone, PieChart, Receipt } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { apiClient, NotificationItem } from "../lib/api";
import {
  getNotificationIcon,
  resolveNotificationUrl,
} from "../lib/notificationNavigation";
import { supabase } from "../lib/supabase";
import { syncWebPushSubscription } from "../lib/webPush";
import { ensureDailyAttendance } from "../lib/supabasePoints";
import { PWAInstallProvider, PWAInstallLink } from "./PWAInstall";

const PoriAssistant = React.lazy(() => import("./pori/PoriAssistant"));

export function AppShell({ children }: { children: React.ReactNode }) {
  const mobileMenuButtonRef = React.useRef<HTMLButtonElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [notifOpen, setNotifOpen] = React.useState(false);
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [notifications, setNotifications] = React.useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = React.useState(0); // notifications unread
  const [unreadMessageCount, setUnreadMessageCount] = React.useState(0);
  const { isAuthenticated, user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isExactAdmin = isAdmin && String(user?.email || "").trim().toLowerCase() === "hwanys2@naver.com";

  // 페이지 이동 시 모바일 메뉴·드롭다운 닫기 (다른 페이지에서 연 메뉴가 콘텐츠를 가리는 것 방지)
  React.useEffect(() => {
    setMobileMenuOpen(false);
    setNotifOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const loadNotifications = React.useCallback(() => {
    if (!isAuthenticated) return;
    apiClient
      .listNotifications({ limit: 5 })
      .then((res) => {
        setNotifications(res.results);
        setUnreadCount(res.unread_count);
      })
      .catch(() => {
        // 헤더 영역이므로 조용히 실패 처리
      });
  }, [isAuthenticated]);

  const handleBellNotificationClick = React.useCallback(
    async (n: NotificationItem) => {
      setNotifOpen(false);
      if (!n.read) {
        try {
          const res = await apiClient.markNotificationRead(n.id);
          setUnreadCount(res.unread_count);
          setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
        } catch {
          // ignore
        }
      }
      const resolved = resolveNotificationUrl(n);
      if (resolved?.isSpa) {
        navigate(resolved.href, { state: ["school_question", "school_answer"].includes(n.app)
          ? { schoolQuestionBackground: location } : undefined });
        return;
      }
      if (resolved) {
        window.open(resolved.href, "_blank", "noopener,noreferrer");
        return;
      }
      navigate("/notifications");
    },
    [navigate, location]
  );

  const loadMessageUnreadCount = React.useCallback(() => {
    if (!isAuthenticated) return;
    apiClient
      .listMessages({ page: 1, limit: 1, folder: "inbox" })
      .then((res) => setUnreadMessageCount(res.unread_count))
      .catch(() => {
        // 헤더 영역이므로 조용히 실패 처리
      });
  }, [isAuthenticated]);

  React.useEffect(() => {
    loadNotifications();
    loadMessageUnreadCount();
    if (!isAuthenticated) return;

    if (user) {
      setIsAdmin(!!user.is_superuser);
    }

    // 폴링 (60초마다 - Realtime이 실패하는 환경의 보조 수단)
    const id = window.setInterval(() => {
      loadNotifications();
      loadMessageUnreadCount();
    }, 60_000);

    // Supabase Realtime: 새 알림 INSERT 시 즉시 카운트 증가 + 목록 갱신
    const sb = supabase;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let channel: any = null;
    if (sb) {
      channel = sb
        .channel('realtime:common_notification')
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'common_notification' },
          () => {
            loadNotifications();
          }
        )
        .subscribe();
    }

    return () => {
      window.clearInterval(id);
      if (channel && sb) {
        sb.removeChannel(channel);
      }
    };
  }, [isAuthenticated, user, loadNotifications, loadMessageUnreadCount]);

  React.useEffect(() => {
    if (authLoading || !isAuthenticated) return;

    const id = window.setTimeout(() => {
      syncWebPushSubscription().catch(() => {
        // 알림 권한 거부/미지원 환경은 조용히 무시
      });
    }, 1500);

    return () => window.clearTimeout(id);
  }, [authLoading, isAuthenticated, user?.id]);

  // 로그인 후 AppShell 진입 시 하루 1회 출석 인정 (KST)
  React.useEffect(() => {
    if (!isAuthenticated) return;
    ensureDailyAttendance().catch(() => {
      // 헤더 영역이므로 조용히 실패 처리
    });
  }, [isAuthenticated, user?.id]);

  React.useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // 파일/이미지 선택: file input 또는 그 label 클릭 시 드롭다운 로직 스킵 (전역에서 파일 창이 안 뜨는 현상 방지)
      if (target.closest?.('input[type="file"]')) return;
      const label = target.closest?.("label");
      if (label && (label.querySelector('input[type="file"]') || (document.getElementById(label.getAttribute("for") || "")?.getAttribute?.("type") === "file"))) return;
      // dropdown 외부 클릭시 닫기(간단 처리)
      if (!target.closest?.("[data-notif-root]")) setNotifOpen(false);
      if (!target.closest?.("[data-user-root]")) setUserMenuOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  React.useEffect(() => {
    if (!mobileMenuOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileMenuOpen]);

  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setNotifOpen(false);
      setUserMenuOpen(false);
      if (mobileMenuOpen) {
        setMobileMenuOpen(false);
        mobileMenuButtonRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [mobileMenuOpen]);

  React.useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1280px)");
    const closeOnDesktop = () => {
      if (desktop.matches) setMobileMenuOpen(false);
    };
    desktop.addEventListener("change", closeOnDesktop);
    return () => desktop.removeEventListener("change", closeOnDesktop);
  }, []);

  const markAllRead = async () => {
    try {
      const res = await apiClient.markAllNotificationsRead();
      setUnreadCount(res.unread_count);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {
      // ignore
    }
  };

  return (
    <ToolSearchProvider shortcutBlocked={mobileMenuOpen || notifOpen || userMenuOpen}>
    <PWAInstallProvider>
    <div className="fe-app min-h-screen" data-workspace={location.pathname.split("/")[1]}>
      <a href="#main-content" className="fe-skip-link">본문으로 바로가기</a>
      {/* 상단 네비게이션 바 */}
      <header className="fe-header fixed top-0 left-0 right-0 z-50 pt-[env(safe-area-inset-top)]">
        <nav className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* 로고 */}
            <NavLink to="/" className="flex items-center gap-3 group">
              <span className="fe-brand-mark" aria-hidden="true">f<span>.</span></span>
              <span className="fe-brand-word">foreducator<span>선생님 곁의 작은 동료</span></span>
            </NavLink>

            {/* 데스크톱 메뉴 */}
            <div className="fe-desktop-nav hidden xl:flex items-center gap-1">
              {NAV_ITEMS.map((item) =>
                item.externalUrl ? (
                  <a
                    key={item.label}
                    href={item.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium transition-all duration-200 relative group text-gray-700 hover:text-[#526a2e]"
                  >
                    {item.label}
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#647c3e] transition-all duration-200 group-hover:w-full"></span>
                  </a>
                ) : (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      cn(
                        "fe-nav-link text-sm font-medium transition-all duration-200 relative group",
                        isActive
                          ? "fe-nav-active"
                          : "text-gray-700 hover:text-[#526a2e]"
                      )
                    }
                  >
                    {item.label}
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#647c3e] transition-all duration-200 group-hover:w-full"></span>
                  </NavLink>
                )
              )}
            </div>

            {/* 우측 버튼 */}
            <div className="flex items-center gap-2 shrink-0">
            <ToolSwitcher header onOpen={() => { setMobileMenuOpen(false); setNotifOpen(false); setUserMenuOpen(false); }} />
            <div className="hidden xl:flex items-center gap-2">
              {isAuthenticated ? (
                <>
                  {/* 쪽지 */}
                  <NavLink
                    to="/messages"
                    className="relative p-2 rounded-xl hover:bg-gray-100 transition-colors"
                    aria-label="messages"
                  >
                    <Mail className="w-5 h-5 text-gray-700" />
                    {unreadMessageCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-600 ring-2 ring-white" />
                    )}
                  </NavLink>

                  {/* 알림 */}
                  <div className="relative" data-notif-root>
                    <button
                      onClick={() => setNotifOpen((v) => !v)}
                      className="relative p-2 rounded-xl hover:bg-gray-100 transition-colors"
                      aria-label="notifications"
                    >
                      <Bell className="w-5 h-5 text-gray-700" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center">
                          {unreadCount > 99 ? "99+" : unreadCount}
                        </span>
                      )}
                    </button>
                    {notifOpen && (
                      <div className="absolute right-0 mt-2 w-96 rounded-2xl border border-[#d5dbca] bg-[#fffefa] shadow-[0_5px_0_#e5e5dc,0_16px_35px_-18px_#25292255] overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                          <div className="text-sm font-semibold text-gray-900">알림</div>
                          <div className="flex items-center gap-3">
                            <button onClick={markAllRead} className="text-sm font-medium text-[#526a2e] hover:text-[#35482a]">
                              모두 읽음
                            </button>
                            <button
                              onClick={() => {
                                setNotifOpen(false);
                                navigate("/notifications");
                              }}
                              className="text-sm font-medium text-gray-700 hover:text-gray-900"
                            >
                              전체보기
                            </button>
                          </div>
                        </div>
                        <div className="max-h-[360px] overflow-auto">
                          {notifications.length === 0 ? (
                            <div className="px-4 py-10 text-center text-sm text-gray-500">알림이 없습니다.</div>
                          ) : (
                            notifications.map((n) => (
                              <button
                                key={n.id}
                                onClick={() => handleBellNotificationClick(n)}
                                className={cn(
                                  "w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors",
                                  !n.read && "bg-[#edf2e2]"
                                )}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm leading-none">{getNotificationIcon(n.app)}</span>
                                      <div className="text-sm font-semibold text-gray-900 truncate">{n.title}</div>
                                    </div>
                                    <div className="text-sm text-gray-600 line-clamp-2 mt-1">{n.message}</div>
                                    {n.url && resolveNotificationUrl(n) && (
                                      <div className="text-xs font-medium text-[#526a2e] mt-1.5">
                                        탭하여 바로 열기
                                      </div>
                                    )}
                                  </div>
                                  {!n.read && <span className="mt-0.5 w-2 h-2 rounded-full bg-[#647c3e] shrink-0" />}
                                </div>
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 유저 메뉴 */}
                  <div className="relative" data-user-root>
                    <button
                      onClick={() => setUserMenuOpen((v) => !v)}
                      className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors"
                    >
                      <User className="w-4 h-4 text-gray-700" />
                      <span className="text-sm font-medium text-gray-800">{getUserDisplayName(user)}</span>
                    </button>
                    {userMenuOpen && (
                      <div className="absolute right-0 mt-2 w-56 rounded-2xl border border-[#d5dbca] bg-[#fffefa] shadow-[0_5px_0_#e5e5dc,0_16px_35px_-18px_#25292255] overflow-hidden">
                        <button
                          onClick={() => {
                            setUserMenuOpen(false);
                            navigate("/messages");
                          }}
                          className="w-full px-4 py-3 text-sm font-medium text-gray-800 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <Mail className="w-4 h-4" />
                          쪽지
                        </button>
                        <button
                          onClick={() => {
                            setUserMenuOpen(false);
                            navigate("/points");
                          }}
                          className="w-full px-4 py-3 text-sm font-medium text-gray-800 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <Coins className="w-4 h-4 text-amber-500" />
                          내 포인트
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => {
                              setUserMenuOpen(false);
                              navigate("/admin/mailing");
                            }}
                            className="w-full px-4 py-3 text-sm font-medium text-[#526a2e] hover:bg-[#edf2e2] flex items-center gap-2"
                          >
                            <Mail className="w-4 h-4" />
                            단체 메일 발송
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => {
                              setUserMenuOpen(false);
                              navigate("/admin/point-grant");
                            }}
                            className="w-full px-4 py-3 text-sm font-medium text-amber-800 hover:bg-amber-50 flex items-center gap-2"
                          >
                            <Gift className="w-4 h-4" />
                            포인트 증정
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => {
                              setUserMenuOpen(false);
                              navigate("/admin/credit-grant");
                            }}
                            className="w-full px-4 py-3 text-sm font-medium text-amber-800 hover:bg-amber-50 flex items-center gap-2"
                          >
                            <Gift className="w-4 h-4" />
                            이용권 증정
                          </button>
                        )}
                        {isExactAdmin && (
                          <button
                            onClick={() => {
                              setUserMenuOpen(false);
                              navigate("/admin/supabase-users");
                            }}
                            className="w-full px-4 py-3 text-sm font-medium text-[#435536] hover:bg-[#edf2e2] flex items-center gap-2"
                          >
                            <BarChart3 className="w-4 h-4" />
                            Supabase 유저 통계
                          </button>
                        )}
                        {isExactAdmin && (
                          <button
                            onClick={() => {
                              setUserMenuOpen(false);
                              navigate("/admin/tool-stats");
                            }}
                            className="w-full px-4 py-3 text-sm font-medium text-emerald-800 hover:bg-emerald-50 flex items-center gap-2"
                          >
                            <Activity className="w-4 h-4" />
                            도구별 사용량 통계
                          </button>
                        )}
                        {isExactAdmin && (
                          <button
                            onClick={() => {
                              setUserMenuOpen(false);
                              navigate("/admin/point-distribution");
                            }}
                            className="w-full px-4 py-3 text-sm font-medium text-amber-900 hover:bg-amber-50 flex items-center gap-2"
                          >
                            <PieChart className="w-4 h-4" />
                            포인트 분포
                          </button>
                        )}
                        {isExactAdmin && (
                          <button
                            onClick={() => {
                              setUserMenuOpen(false);
                              navigate("/admin/home-hero-ad");
                            }}
                            className="w-full px-4 py-3 text-sm font-medium text-[#435536] hover:bg-[#edf2e2] flex items-center gap-2"
                          >
                            <Megaphone className="w-4 h-4" />
                            메인 히어로 광고
                          </button>
                        )}
                        {isExactAdmin && (
                          <button
                            onClick={() => {
                              setUserMenuOpen(false);
                              navigate("/admin/credit-bank");
                            }}
                            className="w-full px-4 py-3 text-sm font-medium text-[#435536] hover:bg-[#edf2e2] flex items-center gap-2"
                          >
                            <Receipt className="w-4 h-4" />
                            이용권 구매 신청
                          </button>
                        )}
                        {isExactAdmin && (
                          <button
                            onClick={() => {
                              setUserMenuOpen(false);
                              navigate("/timetable/admin");
                            }}
                            className="w-full px-4 py-3 text-sm font-medium text-orange-800 hover:bg-orange-50 flex items-center gap-2"
                          >
                            <School className="w-4 h-4" />
                            시간표 전체 학교 조회
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setUserMenuOpen(false);
                            navigate("/account/profile");
                          }}
                          className="w-full px-4 py-3 text-sm font-medium text-gray-800 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <Settings className="w-4 h-4" />
                          프로필 설정
                        </button>
                        <button
                          onClick={() => {
                            setUserMenuOpen(false);
                            navigate("/community/my?tab=posts");
                          }}
                          className="w-full px-4 py-3 text-sm font-medium text-gray-800 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <NotebookPen className="w-4 h-4" />
                          내가 쓴 글
                        </button>
                        <button
                          onClick={() => {
                            setUserMenuOpen(false);
                            navigate("/community/my?tab=commented");
                          }}
                          className="w-full px-4 py-3 text-sm font-medium text-gray-800 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <MessageCircle className="w-4 h-4" />
                          내가 쓴 댓글
                        </button>
                        <button
                          onClick={() => {
                            setUserMenuOpen(false);
                            navigate("/follows?tab=followers");
                          }}
                          className="w-full px-4 py-3 text-sm font-medium text-gray-800 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <Users className="w-4 h-4" />
                          팔로우/팔로잉
                        </button>
                        <button
                          onClick={() => {
                            setUserMenuOpen(false);
                            handleLogout();
                          }}
                          className="w-full px-4 py-3 text-sm font-medium text-red-700 hover:bg-red-50 flex items-center gap-2"
                        >
                          <LogOut className="w-4 h-4" />
                          로그아웃
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <NavLink
                    to="/login"
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-[#526a2e] transition-colors"
                  >
                    로그인
                  </NavLink>
                  <NavLink
                    to="/login"
                    className="fe-button fe-button-dark text-sm"
                  >
                    시작하기
                  </NavLink>
                </>
              )}
            </div>

            {/* 모바일 메뉴 버튼 */}
            <button
              ref={mobileMenuButtonRef}
              aria-label={mobileMenuOpen ? "메뉴 닫기" : "메뉴 열기"}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="xl:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            </div>
          </div>

          {/* 모바일 메뉴 */}
          {mobileMenuOpen && (
            <div
              id="mobile-navigation"
              className="xl:hidden py-4 space-y-2 border-t border-gray-200/50 overflow-y-auto overscroll-contain pb-4"
              style={{ maxHeight: "calc(100dvh - 5rem - env(safe-area-inset-top, 0px))" }}
            >
              {NAV_ITEMS.map((item) =>
                item.externalUrl ? (
                  <a
                    key={item.label}
                    href={item.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-4 py-2 text-sm font-medium rounded-lg transition-colors text-gray-700 hover:bg-gray-50"
                  >
                    {item.label}
                  </a>
                ) : (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        "block px-4 py-2 text-sm font-medium rounded-lg transition-colors",
                        isActive
                          ? "text-[#435536] bg-[#e7edd9]"
                          : "text-gray-700 hover:bg-gray-50"
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                )
              )}
              <div className="pt-4 space-y-2">
                {isAuthenticated ? (
                  <>
                    <div className="px-4 py-2">
                      <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <User className="w-4 h-4" />
                        {getUserDisplayName(user)}
                      </span>
                    </div>
                    {isAdmin && (
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          navigate("/admin/mailing");
                        }}
                        className="w-full px-4 py-2 text-sm font-medium text-[#526a2e] hover:bg-[#edf2e2] rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <Mail className="w-4 h-4" />
                        단체 메일 발송
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          navigate("/admin/point-grant");
                        }}
                        className="w-full px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-50 rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <Gift className="w-4 h-4" />
                        포인트 증정
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          navigate("/admin/credit-grant");
                        }}
                        className="w-full px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-50 rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <Gift className="w-4 h-4" />
                        이용권 증정
                      </button>
                    )}
                    {isExactAdmin && (
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          navigate("/admin/supabase-users");
                        }}
                        className="w-full px-4 py-2 text-sm font-medium text-[#435536] hover:bg-[#edf2e2] rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <BarChart3 className="w-4 h-4" />
                        Supabase 유저 통계
                      </button>
                    )}
                    {isExactAdmin && (
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          navigate("/admin/tool-stats");
                        }}
                        className="w-full px-4 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-50 rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <Activity className="w-4 h-4" />
                        도구별 사용량 통계
                      </button>
                    )}
                    {isExactAdmin && (
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          navigate("/admin/point-distribution");
                        }}
                        className="w-full px-4 py-2 text-sm font-medium text-amber-900 hover:bg-amber-50 rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <PieChart className="w-4 h-4" />
                        포인트 분포
                      </button>
                    )}
                    {isExactAdmin && (
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          navigate("/admin/home-hero-ad");
                        }}
                        className="w-full px-4 py-2 text-sm font-medium text-[#435536] hover:bg-[#edf2e2] rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <Megaphone className="w-4 h-4" />
                        메인 히어로 광고
                      </button>
                    )}
                    {isExactAdmin && (
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          navigate("/admin/credit-bank");
                        }}
                        className="w-full px-4 py-2 text-sm font-medium text-[#435536] hover:bg-[#edf2e2] rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <Receipt className="w-4 h-4" />
                        이용권 구매 신청
                      </button>
                    )}
                    {isExactAdmin && (
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          navigate("/timetable/admin");
                        }}
                        className="w-full px-4 py-2 text-sm font-medium text-orange-800 hover:bg-orange-50 rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <School className="w-4 h-4" />
                        시간표 전체 학교 조회
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        navigate("/notifications");
                      }}
                      className="w-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <Bell className="w-4 h-4" />
                      알림 {unreadCount > 0 ? `(${unreadCount})` : ""}
                    </button>
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        navigate("/messages");
                      }}
                      className="w-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <Mail className="w-4 h-4" />
                      쪽지 {unreadMessageCount > 0 ? `(${unreadMessageCount})` : ""}
                    </button>
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        navigate("/points");
                      }}
                      className="w-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <Coins className="w-4 h-4 text-amber-500" />
                      내 포인트
                    </button>
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        navigate("/account/profile");
                      }}
                      className="w-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <Settings className="w-4 h-4" />
                      프로필 설정
                    </button>
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        navigate("/community/my?tab=posts");
                      }}
                      className="w-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <NotebookPen className="w-4 h-4" />
                      내가 쓴 글
                    </button>
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        navigate("/community/my?tab=commented");
                      }}
                      className="w-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <MessageCircle className="w-4 h-4" />
                      내가 쓴 댓글
                    </button>
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        navigate("/follows?tab=followers");
                      }}
                      className="w-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <Users className="w-4 h-4" />
                      팔로우/팔로잉
                    </button>
                    <button
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="w-full px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      로그아웃
                    </button>
                  </>
                ) : (
                  <>
                    <NavLink
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block w-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors text-center"
                    >
                      로그인
                    </NavLink>
                    <NavLink
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="fe-button fe-button-dark w-full text-sm"
                    >
                      시작하기
                    </NavLink>
                  </>
                )}
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* 메인 컨텐츠 */}
      <main id="main-content" tabIndex={-1} className="fe-main pt-[calc(5rem+env(safe-area-inset-top,0px))] print:pt-0 print:p-0 print:m-0">
        <ToolNavigation />
        {children}
      </main>

      {/* 푸터 */}
      <footer className="fe-footer text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="fe-brand-mark w-10 h-10 flex items-center justify-center">
                  <span className="text-white font-bold">F</span>
                </div>
                <span className="text-xl font-bold text-white">Foreducator</span>
              </div>
              <p className="text-sm text-gray-400 max-w-md">
                {SITE_INFO.description}
              </p>
            </div>
            <div>
              <h2 className="text-sm font-semibold text-white mb-3">Contact</h2>
              <ul className="space-y-2 text-sm">
                {SITE_CONTACTS.map((contact) => (
                  <li key={contact.key}>
                    <span className="text-gray-400">{contact.footerPrefix} : </span>
                    <a href={contact.href} target={contact.href.startsWith("https:") ? "_blank" : undefined} rel="noopener noreferrer" className="hover:text-white transition-colors">
                      {contact.footerLabel}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-sm font-semibold text-white mb-3">개발자의 다른 사이트</h2>
              <ul className="space-y-2 text-sm">
                {DEVELOPER_SITES.map((site) => (
                  <li key={site.key}>
                    <a href={site.href} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                      {site.footerLabel}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-center gap-3 text-sm text-center text-gray-500 flex-wrap">
            <NavLink to="/privacy" className="hover:text-white transition-colors">
              개인정보처리방침
            </NavLink>
            <span className="hidden sm:inline" aria-hidden>
              ·
            </span>
            <span>© 2024 Foreducator. All rights reserved.</span>
            <PWAInstallLink />
          </div>
        </div>
      </footer>
      <React.Suspense fallback={null}>
        <PoriAssistant />
      </React.Suspense>
    </div>
    </PWAInstallProvider>
    </ToolSearchProvider>
  );
}
